import {
    PropsWithChildren,
    startTransition,
    useCallback,
    useMemo,
    useState,
} from "react";
import { useBills } from "@api/bill.api";
import { BillSortValue } from "@common/types/bill";
import { useBillsPageSortingCache } from "@hooks/view/useBillsPageSortingCache";
import { BillFilters } from "@common/contracts/bill.contract";
import {
    useBillsView,
    toBillApiFilters,
} from "../../contexts/BillsViewContext";
import { useSort } from "./useSort";
import {
    BILLS_TABLE_PAGE_SIZE,
    PaginatedBillsTableContext,
    toPaginatedBillsTableContextValue,
} from "./PaginatedBillsTable.context";

interface Props {
    overlayFilters?: BillFilters;
}

export const PaginatedBillsTableContextProvider = ({
    children,
    overlayFilters,
}: PropsWithChildren<Props>) => {
    const [cachedSorting, setCachedSorting] = useBillsPageSortingCache();
    const { filters } = useBillsView();
    const [page, setPage] = useState(1);

    const { sortExpression, currentSort, handleSortChange } =
        useSort(cachedSorting);

    const billsResult = useBills({
        filters: {
            ...toBillApiFilters(filters),
            ...overlayFilters,
        },
        sort: sortExpression,
        page,
        limit: BILLS_TABLE_PAGE_SIZE,
    });

    const { data: bills, isLoading } = billsResult;

    const handleSortChangeWithPageReset = useCallback(
        (value: BillSortValue) => {
            startTransition(() => {
                handleSortChange(value);
                setCachedSorting(value);
                setPage(1);
            });
        },
        [handleSortChange, setCachedSorting, setPage],
    );

    const contextValue = useMemo(
        () =>
            toPaginatedBillsTableContextValue({
                page,
                billsPage: bills,
                setPage,
                handleSortChange: handleSortChangeWithPageReset,
                currentSort,
                isLoading,
            }),
        [page, bills, handleSortChangeWithPageReset, currentSort, isLoading],
    );

    return (
        <PaginatedBillsTableContext.Provider value={contextValue}>
            {children}
        </PaginatedBillsTableContext.Provider>
    );
};
